import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Trophy, Crown, TrendingUp, TrendingDown, Route, Clock, MapPin, Package,
} from "lucide-react";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/theme";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";

const RANK_COLORS = ["#F5C518", "#C0C7D1", "#CD7F32"]; // gold / silver / bronze

export const RANGE_OPTIONS = [
  { key: "month", label: "Month" },
  { key: "quarter", label: "Quarter" },
  { key: "ytd", label: "YTD" },
];

export function RangeToggle({ value, onChange }) {
  return (
    <div className="inline-flex rounded-xl border border-rig-border bg-rig-surface p-1" data-testid="range-toggle">
      {RANGE_OPTIONS.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          data-testid={`range-${o.key}`}
          className={`rounded-lg px-3 py-1.5 font-display text-xs font-bold uppercase tracking-widest transition-colors ${value === o.key ? "bg-rig-accent text-rig-accentText" : "text-rig-dim hover:text-white"}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ProfitValue({ value, className = "" }) {
  const positive = value >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 font-display font-bold ${positive ? "text-rig-accent" : "text-rig-error"} ${className}`}>
      <Icon className="h-4 w-4" />
      {fmtMoney(value)}
    </span>
  );
}

function CompareColumn({ d, isYou }) {
  return (
    <div
      className={`w-[260px] shrink-0 rounded-2xl border bg-rig-surface p-4 ${isYou ? "border-rig-accent" : "border-rig-border"}`}
      data-testid={`compare-col-${d.user_id}`}
    >
      <div className="flex items-center gap-2">
        {d.is_owner && <Crown className="h-4 w-4 shrink-0 text-rig-accent" />}
        <span className="truncate font-display text-base font-bold text-white">{d.name}</span>
        {isYou && <span className="rounded-full bg-rig-accent/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-rig-accent">You</span>}
      </div>

      {/* P&L */}
      <div className="mt-3 rounded-xl border border-rig-border bg-rig-bg p-3">
        <div className="text-[10px] font-bold uppercase tracking-widest text-rig-mute">Profit / Loss</div>
        <ProfitValue value={d.profit} className="mt-0.5 text-xl" />
        <div className="mt-2 flex justify-between text-xs">
          <span className="text-rig-dim">Gross <span className="text-white">{fmtMoney(d.gross)}</span></span>
          <span className="text-rig-dim">Spend <span className="text-white">{fmtMoney(d.spend)}</span></span>
        </div>
      </div>

      {/* Time logs */}
      <div className="mt-3">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-rig-dim">
          <Clock className="h-3.5 w-3.5" /> Time Logs
        </div>
        <p className="mt-1 text-sm text-white">
          {d.trip_count} trip{d.trip_count === 1 ? "" : "s"} · {(d.trip_miles || 0).toLocaleString()} mi
        </p>
        {(d.recent_trips || []).length === 0 ? (
          <p className="mt-1 text-xs text-rig-mute">No trips logged.</p>
        ) : (
          <div className="mt-1 space-y-1">
            {d.recent_trips.slice(0, 3).map((t, i) => (
              <div key={`trip-${t.date || ""}-${t.from || ""}-${t.to || ""}-${i}`} className="flex items-center justify-between text-xs">
                <span className="text-rig-dim">{t.date || "—"}</span>
                <span className="text-white">{t.from || "?"}→{t.to || "?"} · {Math.round(t.miles || 0)} mi</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Travel history */}
      <div className="mt-3">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-rig-dim">
          <MapPin className="h-3.5 w-3.5" /> Travel History
        </div>
        {(d.recent_hauls || []).length === 0 ? (
          <p className="mt-1 text-xs text-rig-mute">No hauls logged.</p>
        ) : (
          <div className="mt-1 space-y-1">
            {d.recent_hauls.slice(0, 3).map((h, i) => (
              <div key={`haul-${h.pickup_city || ""}-${h.delivery_city || ""}-${i}`} className="flex items-center justify-between text-xs">
                <span className="truncate text-white">{h.pickup_city || "—"} → {h.delivery_city || "—"}</span>
                <span className="ml-2 shrink-0 text-rig-accent">{fmtMoney(h.rate_usd || 0)}</span>
              </div>
            ))}
          </div>
        )}
        {(d.miles_by_state || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {d.miles_by_state.slice(0, 5).map((s) => (
              <span key={s.state} className="rounded-md border border-rig-border bg-rig-bg px-2 py-0.5 text-[11px] text-rig-dim">
                {s.state} <span className="text-white">{Math.round(s.miles)}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function ComparePanel({ isOwner, range = "ytd" }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api(`/fleet/compare?range=${range}`)); }
    catch (e) { setData({ error: e?.message || "Could not load leaderboard" }); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const toggleShare = async (val) => {
    setSaving(true);
    try {
      await api("/fleet/settings", { method: "PUT", body: { share_compare: val } });
      setData((d) => ({ ...d, share_compare: val }));
      toast.success(val ? "Drivers can now see the leaderboard" : "Leaderboard hidden from drivers");
    } catch (e) {
      toast.error(e?.message || "Could not update sharing");
    } finally {
      setSaving(false);
    }
  };

  if (data === null) return <Skeleton className="h-64 w-full bg-rig-surface2" />;
  if (data.error) {
    return (
      <div className="rounded-2xl border border-rig-border bg-rig-surface p-8 text-center text-sm text-rig-dim" data-testid="compare-error">
        {data.error}
      </div>
    );
  }

  const drivers = data.drivers || [];
  const viewerId = data.viewer_id;

  return (
    <div className="space-y-6" data-testid="compare-panel">
      {isOwner && (
        <div className="flex items-center justify-between rounded-2xl border border-rig-border bg-rig-surface p-4" data-testid="share-compare-row">
          <div className="min-w-0 pr-4">
            <p className="font-display text-sm font-bold text-white">Let drivers see this leaderboard</p>
            <p className="mt-0.5 text-xs text-rig-dim">When on, every driver can view the fleet ranking and their own row.</p>
          </div>
          <Switch
            checked={!!data.share_compare}
            onCheckedChange={toggleShare}
            disabled={saving}
            data-testid="share-compare-toggle"
            className="data-[state=checked]:bg-rig-accent"
          />
        </div>
      )}

      {drivers.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-rig-borderStrong bg-rig-surface p-10 text-center" data-testid="compare-empty">
          <Trophy className="mx-auto h-8 w-8 text-rig-mute" />
          <p className="mt-3 text-sm text-rig-dim">No drivers to compare yet.</p>
        </div>
      ) : (
        <>
          {/* Leaderboard */}
          <div>
            <div className="mb-3 flex items-center gap-2">
              <Trophy className="h-4 w-4 text-rig-accent" />
              <p className="font-display text-xs font-bold uppercase tracking-[0.18em] text-rig-dim">
                Leaderboard · Profit ({(data.range || "ytd").toUpperCase()})
              </p>
            </div>
            <div className="space-y-2" data-testid="leaderboard">
              {drivers.map((d) => {
                const isYou = d.user_id === viewerId;
                return (
                  <div
                    key={d.user_id}
                    className={`flex items-center gap-3 rounded-xl border bg-rig-surface p-3.5 ${isYou ? "border-rig-accent" : "border-rig-border"}`}
                    data-testid={`leaderboard-row-${d.user_id}`}
                  >
                    <div
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-display text-sm font-bold"
                      style={{
                        background: RANK_COLORS[d.rank - 1] ? `${RANK_COLORS[d.rank - 1]}22` : "#1E293B",
                        color: RANK_COLORS[d.rank - 1] || "#94A3B8",
                      }}
                    >
                      {d.rank}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {d.is_owner && <Crown className="h-3.5 w-3.5 shrink-0 text-rig-accent" />}
                        <span className="truncate font-display text-base font-bold text-white">{d.name}</span>
                        {isYou && <span className="rounded-full bg-rig-accent/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-rig-accent">You</span>}
                      </div>
                      <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-rig-dim">
                        <span className="inline-flex items-center gap-1"><Package className="h-3 w-3" />{fmtMoney(d.gross)}</span>
                        <span className="inline-flex items-center gap-1"><Route className="h-3 w-3" />{(d.miles || 0).toLocaleString()} mi</span>
                        <span>{d.receipt_count} receipts</span>
                      </div>
                    </div>
                    <ProfitValue value={d.profit} className="text-lg" />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Side-by-side comparison */}
          <div>
            <p className="mb-3 font-display text-xs font-bold uppercase tracking-[0.18em] text-rig-dim">
              Side-by-Side Comparison
            </p>
            <div className="flex gap-3 overflow-x-auto pb-2" data-testid="compare-scroller">
              {drivers.map((d) => (
                <CompareColumn key={d.user_id} d={d} isYou={d.user_id === viewerId} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
